import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from './root';
import { NotificationManager } from '../components/notification-manager';
import { HttpMethod } from '../types';

export interface CarWashStore {
    carwashStoreId: number;
    storeId: string;
    storeNref: string;
    address: string;
    city: string;
    isSoftTouch: boolean;
    isUltimate: boolean;
    isTouchFree: boolean;
    isUltimatePlus: boolean;
}

export interface CarWashStoreInput {
    storeNref: string;
    address: string;
    city: string;
    isSoftTouch: boolean;
    isUltimate: boolean;
    isTouchFree: boolean;
    isUltimatePlus: boolean;
}

export interface CarwashStat {
    name: string;
    value: number;
}

export interface CarwashStatsResponse {
    stats: CarwashStat[];
}

export class CarWashStore {
    rootStore: RootStore;
    carwashStores: CarWashStore[] = [];
    isFetchingStores = false;
    isUpdatingStore = false;
    isDeletingStore = false;
    transactionStats: CarwashStat[] | null = null;
    isFetchingTransactionStats = false;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this);
    }

    async fetchCarWashStores(params?: {
        storeNref?: string;
        city?: string;
        isSoftTouch?: boolean;
        isUltimate?: boolean;
        isTouchFree?: boolean;
    }) {
        this.isFetchingStores = true;
        try {
            const response = await this.rootStore.makeNetworkCall<
                CarWashStore[]
            >({
                method: HttpMethod.GET,
                url: '/stores/carwash',
                params,
            });
            runInAction(() => {
                if (response.data) {
                    this.carwashStores = response.data;
                }
            });
        } finally {
            runInAction(() => {
                this.isFetchingStores = false;
            });
        }
    }

    async createCarWashStore(payload: CarWashStoreInput) {
        this.isUpdatingStore = true;
        try {
            const response = await this.rootStore.makeNetworkCall<CarWashStore>(
                {
                    method: HttpMethod.POST,
                    url: '/stores/carwash',
                    data: payload,
                }
            );
            if (!response.err) {
                await this.fetchCarWashStores();
                NotificationManager.showSuccess(
                    'Carwash store created successfully'
                );
                return response.data;
            } else {
                NotificationManager.showError('Failed to create carwash store');
            }
        } finally {
            runInAction(() => {
                this.isUpdatingStore = false;
            });
        }
    }

    async updateCarWashStore(id: number, payload: CarWashStoreInput) {
        this.isUpdatingStore = true;
        try {
            const response = await this.rootStore.makeNetworkCall<CarWashStore>(
                {
                    method: HttpMethod.PUT,
                    url: `/stores/carwash/${id}`,
                    data: payload,
                }
            );
            if (!response.err) {
                await this.fetchCarWashStores();
                NotificationManager.showSuccess(
                    'Carwash store updated successfully'
                );
                return response.data;
            } else {
                NotificationManager.showError('Failed to update carwash store');
            }
        } finally {
            runInAction(() => {
                this.isUpdatingStore = false;
            });
        }
    }

    async fetchTransactionStats(params?: {
        startDate?: string;
        endDate?: string;
    }) {
        this.isFetchingTransactionStats = true;
        try {
            const response = await this.rootStore.makeNetworkCall<
                CarwashStatsResponse
            >({
                method: HttpMethod.GET,
                url: '/car-wash/transactions/stats',
                params,
            });
            runInAction(() => {
                if (response.data) {
                    this.transactionStats = response.data.stats;
                }
            });
            return response.data;
        } finally {
            runInAction(() => {
                this.isFetchingTransactionStats = false;
            });
        }
    }
}
